import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import permissionService from '../services/permissionService';
import './SideNav.css';

const SideNav = ({ isMobileOpen, onClose }) => {
  const [isSetupExpanded, setIsSetupExpanded] = useState(false);
  const [isOnlineExpanded, setIsOnlineExpanded] = useState(false);
  const [isBankExpanded, setIsBankExpanded] = useState(false);
  const [isStocksExpanded, setIsStocksExpanded] = useState(false);
  const [permissions, setPermissions] = useState({
    'users.read': false,
    'roles.read': false,
    'permissions.read': false,
    'salesorders.read': false,
    'purchaseorders.read': false,
    'expenses.read': false,
    'menus.read': false,
    'products.read': false,
    'packs.read': false,
    'online.approve': false,
    'online.orders.read': false,
    'online.orders.myorder': false,
    'online.referrals': false,
    'online.reconcile': false,
    'online.offers': false,
    'bank.transactions.upload': false,
    'stocks.locations':    false,
    'stocks.my_stock':     false,
    'stocks.product_flow': false,
  });
  const location = useLocation();

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const permissionChecks = {
      'users.read': false,
      'roles.read': false,
      'permissions.read': false,
      'salesorders.read': false,
      'purchaseorders.read': false,
      'expenses.read': false,
      'menus.read': false,
      'products.read': false,
      'packs.read': false,
      'online.approve': false,
      'online.orders.read': false,
      'online.orders.myorder': false,
      'online.referrals': false,
      'online.reconcile': false,
      'online.offers': false,
      'bank.transactions.upload': false,
      'stocks.locations':    false,
      'stocks.my_stock':     false,
      'stocks.product_flow': false,
    };

    try {
      // Check each permission
      for (const permName of Object.keys(permissionChecks)) {
        try {
          const result = await permissionService.checkPermission(permName);
          if (result.success) {
            permissionChecks[permName] = result.hasPermission;
          }
        } catch (err) {
          console.error(`Failed to check permission ${permName}:`, err);
        }
      }
      setPermissions(permissionChecks);
    } catch (err) {
      console.error('Failed to check permissions:', err);
    }
  };

  const toggleSetup = () => {
    setIsSetupExpanded(!isSetupExpanded);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if user has any setup permissions
  const hasAnySetupPermission = permissions['users.read'] || permissions['roles.read'] || permissions['permissions.read'] || permissions['menus.read'] || permissions['products.read'] || permissions['packs.read'];
  const hasAnyOnlinePermission = permissions['online.approve'] || permissions['online.orders.read'] || permissions['online.orders.myorder'] || permissions['online.referrals'] || permissions['online.reconcile'] || permissions['online.offers'];
  const hasAnyBankPermission   = permissions['bank.transactions.upload'];
  const hasAnyStocksPermission = permissions['stocks.locations'] || permissions['stocks.my_stock'] || permissions['stocks.product_flow'];

  // Handle link clicks on mobile - close menu
  const handleLinkClick = () => {
    if (onClose && window.innerWidth <= 768) {
      onClose();
    }
  };

  return (
    <nav className={`side-nav ${isMobileOpen ? 'mobile-open' : ''}`}>
      <ul className="nav-menu">
        {/* Home Link */}
        <li className={`nav-item ${isActive('/home') ? 'active' : ''}`}>
          <Link to="/home" className="nav-link" onClick={handleLinkClick}>
            <span className="nav-icon">🏠</span>
            <span className="nav-text">Home</span>
          </Link>
        </li>

        {/* Sales Order Link */}
        {permissions['salesorders.read'] && (
          <li className={`nav-item ${isActive('/sales-orders') ? 'active' : ''}`}>
            <Link to="/sales-orders" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">📦</span>
              <span className="nav-text">Sales Orders</span>
            </Link>
          </li>
        )}

        {/* Purchase Order Link */}
        {permissions['purchaseorders.read'] && (
          <li className={`nav-item ${isActive('/purchase-orders') ? 'active' : ''}`}>
            <Link to="/purchase-orders" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">🛒</span>
              <span className="nav-text">Purchase Orders</span>
            </Link>
          </li>
        )}

        {/* Expenses Link */}
        {permissions['expenses.read'] && (
          <li className={`nav-item ${isActive('/expenses') ? 'active' : ''}`}>
            <Link to="/expenses" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">💰</span>
              <span className="nav-text">Expenses</span>
            </Link>
          </li>
        )}

        {/* Setup Menu - Only show if user has at least one setup permission */}
        {hasAnySetupPermission && (
          <li className="nav-item">
            <div
              className={`nav-link expandable ${isSetupExpanded ? 'expanded' : ''}`}
              onClick={toggleSetup}
            >
              <span className="nav-icon">⚙️</span>
              <span className="nav-text">Setup</span>
              <span className={`expand-arrow ${isSetupExpanded ? 'rotated' : ''}`}>▼</span>
            </div>

            {/* Setup Submenu */}
            <ul className={`nav-submenu ${isSetupExpanded ? 'show' : ''}`}>
              {permissions['users.read'] && (
                <li className={`nav-subitem ${isActive('/setup/users') ? 'active' : ''}`}>
                  <Link to="/setup/users" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">👤</span>
                    <span className="nav-subtext">Users</span>
                  </Link>
                </li>
              )}

              {permissions['roles.read'] && (
                <li className={`nav-subitem ${isActive('/setup/roles') ? 'active' : ''}`}>
                  <Link to="/setup/roles" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🎭</span>
                    <span className="nav-subtext">Roles</span>
                  </Link>
                </li>
              )}

              {permissions['permissions.read'] && (
                <li className={`nav-subitem ${isActive('/setup/permissions') ? 'active' : ''}`}>
                  <Link to="/setup/permissions" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🔐</span>
                    <span className="nav-subtext">Permissions</span>
                  </Link>
                </li>
              )}

              {permissions['menus.read'] && (
                <li className={`nav-subitem ${isActive('/setup/menu-admin') ? 'active' : ''}`}>
                  <Link to="/setup/menu-admin" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🗂️</span>
                    <span className="nav-subtext">Menu Admin</span>
                  </Link>
                </li>
              )}
              {permissions['products.read'] && (
                <li className={`nav-subitem ${isActive('/setup/products') ? 'active' : ''}`}>
                  <Link to="/setup/products" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📦</span>
                    <span className="nav-subtext">Products</span>
                  </Link>
                </li>
              )}
              {permissions['packs.read'] && (
                <li className={`nav-subitem ${isActive('/setup/product-packs') ? 'active' : ''}`}>
                  <Link to="/setup/product-packs" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🎁</span>
                    <span className="nav-subtext">Product Packs</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}
        {/* Online Menu */}
        {hasAnyOnlinePermission && (
          <li className="nav-item">
            <div
              className={`nav-link expandable ${isOnlineExpanded ? 'expanded' : ''}`}
              onClick={() => setIsOnlineExpanded(!isOnlineExpanded)}
            >
              <span className="nav-icon">🌐</span>
              <span className="nav-text">Online</span>
              <span className={`expand-arrow ${isOnlineExpanded ? 'rotated' : ''}`}>▼</span>
            </div>

            <ul className={`nav-submenu ${isOnlineExpanded ? 'show' : ''}`}>
              {permissions['online.approve'] && (
                <li className={`nav-subitem ${isActive('/online/approve-customers') ? 'active' : ''}`}>
                  <Link to="/online/approve-customers" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">✅</span>
                    <span className="nav-subtext">Approve Customer</span>
                  </Link>
                </li>
              )}
              {(permissions['online.orders.read'] || permissions['online.orders.myorder']) && (
                <li className={`nav-subitem ${isActive('/online/orders') ? 'active' : ''}`}>
                  <Link to="/online/orders" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🛍️</span>
                    <span className="nav-subtext">Orders</span>
                  </Link>
                </li>
              )}
              {permissions['online.referrals'] && (
                <li className={`nav-subitem ${isActive('/online/referrals') ? 'active' : ''}`}>
                  <Link to="/online/referrals" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🤝</span>
                    <span className="nav-subtext">Partners</span>
                  </Link>
                </li>
              )}
              {permissions['online.referrals'] && (
                <li className={`nav-subitem ${isActive('/online/commission-payout') ? 'active' : ''}`}>
                  <Link to="/online/commission-payout" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">💳</span>
                    <span className="nav-subtext">Commission Payout</span>
                  </Link>
                </li>
              )}
              {permissions['online.reconcile'] && (
                <li className={`nav-subitem ${isActive('/online/reconcile') ? 'active' : ''}`}>
                  <Link to="/online/reconcile" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🔗</span>
                    <span className="nav-subtext">Reconcile</span>
                  </Link>
                </li>
              )}
              {permissions['online.offers'] && (
                <li className={`nav-subitem ${isActive('/online/offers') ? 'active' : ''}`}>
                  <Link to="/online/offers" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🎁</span>
                    <span className="nav-subtext">Offers</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* Bank Menu */}
        {hasAnyBankPermission && (
          <li className="nav-item">
            <div
              className={`nav-link expandable ${isBankExpanded ? 'expanded' : ''}`}
              onClick={() => setIsBankExpanded(!isBankExpanded)}
            >
              <span className="nav-icon">🏦</span>
              <span className="nav-text">Bank</span>
              <span className={`expand-arrow ${isBankExpanded ? 'rotated' : ''}`}>▼</span>
            </div>
            <ul className={`nav-submenu ${isBankExpanded ? 'show' : ''}`}>
              {permissions['bank.transactions.upload'] && (
                <li className={`nav-subitem ${isActive('/bank/transactions') ? 'active' : ''}`}>
                  <Link to="/bank/transactions" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📂</span>
                    <span className="nav-subtext">Transactions</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* Stocks Menu */}
        {hasAnyStocksPermission && (
          <li className="nav-item">
            <div
              className={`nav-link expandable ${isStocksExpanded ? 'expanded' : ''}`}
              onClick={() => setIsStocksExpanded(!isStocksExpanded)}
            >
              <span className="nav-icon">🏭</span>
              <span className="nav-text">Stocks</span>
              <span className={`expand-arrow ${isStocksExpanded ? 'rotated' : ''}`}>▼</span>
            </div>

            <ul className={`nav-submenu ${isStocksExpanded ? 'show' : ''}`}>
              {permissions['stocks.my_stock'] && (
                <li className={`nav-subitem ${isActive('/stocks/my-stock') ? 'active' : ''}`}>
                  <Link to="/stocks/my-stock" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📦</span>
                    <span className="nav-subtext">My Stock</span>
                  </Link>
                </li>
              )}
              {permissions['stocks.locations'] && (
                <li className={`nav-subitem ${isActive('/stocks/locations') ? 'active' : ''}`}>
                  <Link to="/stocks/locations" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📍</span>
                    <span className="nav-subtext">Locations</span>
                  </Link>
                </li>
              )}
              {permissions['stocks.product_flow'] && (
                <li className={`nav-subitem ${isActive('/stocks/product-flow') ? 'active' : ''}`}>
                  <Link to="/stocks/product-flow" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🔀</span>
                    <span className="nav-subtext">Product Flow</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}
      </ul>
    </nav>
  );
};

export default SideNav;
